import React, { FC, useState } from 'react';
import Subheading from '../font/Subheading';

import styled from 'styled-components';
import Spacer from '../layout/Spacer';
import Pill from '../navigation/Pill';
import { useAppDispatch, useAppSelector } from '../../redux/redux.hooks';
import ImageAnswer from './ImageAnswer';
import {
  changeFemaleBellyShape,
  changeFemaleChestShape,
  changeFemaleHipShape,
  changeMaleBellyShape,
  changeMaleChestShape,
  changeMaleHipShape,
} from '../../reducers/fit.quiz.widget.reducer';

type BodyShapesProps = {};

const BodyShapes: FC<BodyShapesProps> = () => {
  const dispatch = useAppDispatch();
  const [isMale, setIsMale] = useState(true);
  const {
    maleChestShape,
    femaleChestShape,
    maleBellyShape,
    femaleBellyShape,
    maleHipShape,
    femaleHipShape,
  } = useAppSelector((state) => state.widgetReducer.data);

  const renderNavItems = () => (
    <>
      <Pill
        key={`k-male`}
        backgroundColor={isMale ? '#35b8583d' : '#fafafa66'}
        children={<Subheading>Male</Subheading>}
        onClick={() => {
          setIsMale(true);
        }}
      />
      <Spacer width={12} />
      <Pill
        key={`k-female`}
        backgroundColor={!isMale ? '#35b8583d' : '#fafafa66'}
        children={<Subheading>Female</Subheading>}
        onClick={() => {
          setIsMale(false);
        }}
      />
    </>
  );

  const renderChestShape = () => {
    const maleSlim = maleChestShape.slim;
    const maleAverage = maleChestShape.average;
    const maleBroad = maleChestShape.broad;
    const femaleSlim = femaleChestShape.slim;
    const femaleAverage = femaleChestShape.average;
    const femaleBroad = femaleChestShape.broad;
    return (
      <ImageAnswer
        sectionTitle='Chest shape'
        firstImageTitle={isMale ? maleSlim.imageTitle : femaleSlim.imageTitle}
        firstImage={isMale ? maleSlim.imageUrl : femaleSlim.imageUrl}
        firstImageChanged={(value: string) =>
          dispatch(
            isMale
              ? changeMaleChestShape({
                  ...maleChestShape,
                  slim: { ...maleSlim, imageUrl: value },
                })
              : changeFemaleChestShape({
                  ...femaleChestShape,
                  slim: { ...femaleSlim, imageUrl: value },
                })
          )
        }
        resetFirstImage={() =>
          dispatch(
            isMale
              ? changeMaleChestShape({
                  ...maleChestShape,
                  slim: { ...maleSlim, imageUrl: maleSlim.defaultImageUrl },
                })
              : changeFemaleChestShape({
                  ...femaleChestShape,
                  slim: { ...femaleSlim, imageUrl: femaleSlim.defaultImageUrl },
                })
          )
        }
        secondImageTitle={
          isMale ? maleAverage.imageTitle : femaleAverage.imageTitle
        }
        secondImage={isMale ? maleAverage.imageUrl : femaleAverage.imageUrl}
        secondImageChanged={(value: string) =>
          dispatch(
            isMale
              ? changeMaleChestShape({
                  ...maleChestShape,
                  average: { ...maleAverage, imageUrl: value },
                })
              : changeFemaleChestShape({
                  ...femaleChestShape,
                  average: { ...femaleAverage, imageUrl: value },
                })
          )
        }
        resetSecondImage={() =>
          dispatch(
            isMale
              ? changeMaleChestShape({
                  ...maleChestShape,
                  average: {
                    ...maleAverage,
                    imageUrl: maleAverage.defaultImageUrl,
                  },
                })
              : changeFemaleChestShape({
                  ...femaleChestShape,
                  average: {
                    ...femaleAverage,
                    imageUrl: femaleAverage.defaultImageUrl,
                  },
                })
          )
        }
        thirdImageTitle={isMale ? maleBroad.imageTitle : femaleBroad.imageTitle}
        thirdImage={isMale ? maleBroad.imageUrl : femaleBroad.imageUrl}
        thirdImageChanged={(value: string) =>
          dispatch(
            isMale
              ? changeMaleChestShape({
                  ...maleChestShape,
                  broad: { ...maleBroad, imageUrl: value },
                })
              : changeFemaleChestShape({
                  ...femaleChestShape,
                  broad: { ...femaleBroad, imageUrl: value },
                })
          )
        }
        resetThirdImage={() =>
          dispatch(
            isMale
              ? changeMaleChestShape({
                  ...maleChestShape,
                  broad: { ...maleBroad, imageUrl: maleBroad.defaultImageUrl },
                })
              : changeFemaleChestShape({
                  ...femaleChestShape,
                  broad: {
                    ...femaleBroad,
                    imageUrl: femaleBroad.defaultImageUrl,
                  },
                })
          )
        }
      />
    );
  };

  const renderBellyShape = () => {
    const maleFlat = maleBellyShape.flat;
    const maleAverage = maleBellyShape.average;
    const maleRound = maleBellyShape.round;
    const femaleFlat = femaleBellyShape.flat;
    const femaleAverage = femaleBellyShape.average;
    const femaleRound = femaleBellyShape.round;
    return (
      <ImageAnswer
        sectionTitle='Belly shape'
        firstImageTitle={isMale ? maleFlat.imageTitle : femaleFlat.imageTitle}
        firstImage={isMale ? maleFlat.imageUrl : femaleFlat.imageUrl}
        firstImageChanged={(value: string) =>
          dispatch(
            isMale
              ? changeMaleBellyShape({
                  ...maleBellyShape,
                  flat: { ...maleFlat, imageUrl: value },
                })
              : changeFemaleBellyShape({
                  ...femaleBellyShape,
                  flat: { ...femaleFlat, imageUrl: value },
                })
          )
        }
        resetFirstImage={() =>
          dispatch(
            isMale
              ? changeMaleBellyShape({
                  ...maleBellyShape,
                  flat: { ...maleFlat, imageUrl: maleFlat.defaultImageUrl },
                })
              : changeFemaleBellyShape({
                  ...femaleBellyShape,
                  flat: { ...femaleFlat, imageUrl: femaleFlat.defaultImageUrl },
                })
          )
        }
        secondImageTitle={
          isMale ? maleAverage.imageTitle : femaleAverage.imageTitle
        }
        secondImage={isMale ? maleAverage.imageUrl : femaleAverage.imageUrl}
        secondImageChanged={(value: string) =>
          dispatch(
            isMale
              ? changeMaleBellyShape({
                  ...maleBellyShape,
                  average: { ...maleAverage, imageUrl: value },
                })
              : changeFemaleBellyShape({
                  ...femaleBellyShape,
                  average: { ...femaleAverage, imageUrl: value },
                })
          )
        }
        resetSecondImage={() =>
          dispatch(
            isMale
              ? changeMaleBellyShape({
                  ...maleBellyShape,
                  average: {
                    ...maleAverage,
                    imageUrl: maleAverage.defaultImageUrl,
                  },
                })
              : changeFemaleBellyShape({
                  ...femaleBellyShape,
                  average: {
                    ...femaleAverage,
                    imageUrl: femaleAverage.defaultImageUrl,
                  },
                })
          )
        }
        thirdImageTitle={isMale ? maleRound.imageTitle : femaleRound.imageTitle}
        thirdImage={isMale ? maleRound.imageUrl : femaleRound.imageUrl}
        thirdImageChanged={(value: string) =>
          dispatch(
            isMale
              ? changeMaleBellyShape({
                  ...maleBellyShape,
                  round: { ...maleRound, imageUrl: value },
                })
              : changeFemaleBellyShape({
                  ...femaleBellyShape,
                  round: { ...femaleRound, imageUrl: value },
                })
          )
        }
        resetThirdImage={() =>
          dispatch(
            isMale
              ? changeMaleBellyShape({
                  ...maleBellyShape,
                  round: { ...maleRound, imageUrl: maleRound.defaultImageUrl },
                })
              : changeFemaleBellyShape({
                  ...femaleBellyShape,
                  round: {
                    ...femaleRound,
                    imageUrl: femaleRound.defaultImageUrl,
                  },
                })
          )
        }
      />
    );
  };

  const renderHipShape = () => {
    const maleSlim = maleHipShape.slim;
    const maleAverage = maleHipShape.average;
    const maleWide = maleHipShape.wide;
    const femaleSlim = femaleHipShape.slim;
    const femaleAverage = femaleHipShape.average;
    const femaleWide = femaleHipShape.wide;
    return (
      <ImageAnswer
        sectionTitle='Hip shape'
        firstImageTitle={isMale ? maleSlim.imageTitle : femaleSlim.imageTitle}
        firstImage={isMale ? maleSlim.imageUrl : femaleSlim.imageUrl}
        firstImageChanged={(value: string) =>
          dispatch(
            isMale
              ? changeMaleHipShape({
                  ...maleHipShape,
                  slim: { ...maleSlim, imageUrl: value },
                })
              : changeFemaleHipShape({
                  ...femaleHipShape,
                  slim: { ...femaleSlim, imageUrl: value },
                })
          )
        }
        resetFirstImage={() =>
          dispatch(
            isMale
              ? changeMaleHipShape({
                  ...maleHipShape,
                  slim: { ...maleSlim, imageUrl: maleSlim.defaultImageUrl },
                })
              : changeFemaleHipShape({
                  ...femaleHipShape,
                  slim: { ...femaleSlim, imageUrl: femaleSlim.defaultImageUrl },
                })
          )
        }
        secondImageTitle={
          isMale ? maleAverage.imageTitle : femaleAverage.imageTitle
        }
        secondImage={isMale ? maleAverage.imageUrl : femaleAverage.imageUrl}
        secondImageChanged={(value: string) =>
          dispatch(
            isMale
              ? changeMaleHipShape({
                  ...maleHipShape,
                  average: { ...maleAverage, imageUrl: value },
                })
              : changeFemaleHipShape({
                  ...femaleHipShape,
                  average: { ...femaleAverage, imageUrl: value },
                })
          )
        }
        resetSecondImage={() =>
          dispatch(
            isMale
              ? changeMaleHipShape({
                  ...maleHipShape,
                  average: {
                    ...maleAverage,
                    imageUrl: maleAverage.defaultImageUrl,
                  },
                })
              : changeFemaleHipShape({
                  ...femaleHipShape,
                  average: {
                    ...femaleAverage,
                    imageUrl: femaleAverage.defaultImageUrl,
                  },
                })
          )
        }
        thirdImageTitle={isMale ? maleWide.imageTitle : femaleWide.imageTitle}
        thirdImage={isMale ? maleWide.imageUrl : femaleWide.imageUrl}
        thirdImageChanged={(value: string) =>
          dispatch(
            isMale
              ? changeMaleHipShape({
                  ...maleHipShape,
                  wide: { ...maleWide, imageUrl: value },
                })
              : changeFemaleHipShape({
                  ...femaleHipShape,
                  wide: { ...femaleWide, imageUrl: value },
                })
          )
        }
        resetThirdImage={() =>
          dispatch(
            isMale
              ? changeMaleHipShape({
                  ...maleHipShape,
                  wide: { ...maleWide, imageUrl: maleWide.defaultImageUrl },
                })
              : changeFemaleHipShape({
                  ...femaleHipShape,
                  wide: { ...femaleWide, imageUrl: femaleWide.defaultImageUrl },
                })
          )
        }
      />
    );
  };

  return (
    <>
      <TabContainer>{renderNavItems()}</TabContainer>
      <Spacer height={12} />
      {renderBellyShape()}
      <Spacer height={12} />
      {renderChestShape()}
      <Spacer height={12} />
      {renderHipShape()}
    </>
  );
};

export default BodyShapes;

const TabContainer = styled.div`
  width: 100%;
  height: auto;
  display: flex;
  justify-content: center;
  align-items: center;
`;
